import { createContext, useContext, useState, useMemo } from "react";
import tracking from "frontend/tracking";

type LibraryState = {
  isExpanded?: boolean;
  isError?: boolean;
};

type LibraryStatesContextType = {
  libraryStates: Record<string, LibraryState>;
  toggleLibraryExpansion: (libraryId: string) => void;
  setLibraryExpansion: (libraryId: string, expanded: boolean) => void;
  isLibraryExpanded: (libraryId: string) => boolean;
  setLibraryError: (libraryId: string, error: boolean) => void;
  isLibraryError: (libraryId: string) => boolean;
};

const LibraryExpansionContext = createContext<LibraryStatesContextType | null>(null);

export function LibraryStatesProvider({ children }: { children: React.ReactNode }) {
  const [libraryStates, setLibraryStates] = useState<Record<string, LibraryState>>({
    basicShapes: { isExpanded: true },
  });

  const value = useMemo(
    () => ({
      libraryStates,
      toggleLibraryExpansion: (libraryId: string, track = true) => {
        if (track && !libraryStates[libraryId]?.isExpanded)
          tracking.trackAnalyticsEvent("shape-library-expanded", { libraryId });
        setLibraryStates((previous) => ({
          ...previous,
          [libraryId]: { ...previous[libraryId], isExpanded: !previous[libraryId]?.isExpanded },
        }));
      },
      setLibraryExpansion: (libraryId: string, expanded: boolean) => {
        if (libraryStates[libraryId]?.isExpanded === expanded) return;
        setLibraryStates((previous) => ({
          ...previous,
          [libraryId]: { ...previous[libraryId], isExpanded: expanded },
        }));
      },
      isLibraryExpanded: (libraryId: string) => libraryStates[libraryId]?.isExpanded ?? false,
      setLibraryError: (libraryId: string, error: boolean) => {
        if (libraryStates[libraryId]?.isError === error) return;
        setLibraryStates((previous) => ({
          ...previous,
          [libraryId]: { ...previous[libraryId], isError: error },
        }));
      },
      isLibraryError: (libraryId: string) => libraryStates[libraryId]?.isError ?? false,
    }),
    [libraryStates]
  );

  return <LibraryExpansionContext.Provider value={value}>{children}</LibraryExpansionContext.Provider>;
}

export function useLibraryStates() {
  const context = useContext(LibraryExpansionContext);
  if (!context) {
    throw new Error("useLibraryStates must be used within a LibraryStatesProvider");
  }
  return context;
}
